import { profileRemoteFeedback, profileFeedbackText, type ProfileRemoteFeedback } from "./profileFeedbackPresentation";
import { notificationRemoteFeedback, notificationFeedbackText, isNotificationRemoteFeedback, type NotificationRemoteFeedback } from "./notificationFeedbackPresentation";
import type { TFunction } from "i18next";

export type NotificationErrorKey =
  | "securityToken" | "loadFailed" | "markReadFailed" | "markUnreadFailed"
  | "dismissFailed" | "preferencesLoadFailed" | "preferencesSaveFailed"
  | "actionNotAllowed" | "signInAgain";

export type NotificationFeedback = { key: NotificationErrorKey; message?: never } | { message: string; key?: never } | NotificationRemoteFeedback | ProfileRemoteFeedback;

/** Preserve server diagnostics; only locally authored fallbacks have translation keys. */
export function notificationResponseFeedback(response: unknown, fallback: NotificationErrorKey): NotificationFeedback {
  const profile = profileRemoteFeedback(response);
  if (profile) return profile;
  const remote = notificationRemoteFeedback(response);
  if (remote) return remote;
  if (response && typeof response === "object") {
    const body = response as { error?: unknown; message?: unknown };
    const nested = body.error && typeof body.error === "object" ? (body.error as { message?: unknown }).message : undefined;
    for (const value of [nested, body.message]) {
      if (typeof value === "string" && value.trim()) return { message: value };
    }
  }
  return { key: fallback };
}

export function notificationCaughtFeedback(error: unknown, fallback: NotificationErrorKey): NotificationFeedback {
  if (error instanceof NotificationFeedbackError) return error.feedback;
  return error instanceof Error && error.message ? { message: error.message } : { key: fallback };
}

export function presentNotificationFeedback(feedback: NotificationFeedback, t: TFunction<"notifications">): string {
  if ("profileRemote" in feedback) return profileFeedbackText(feedback);
  if (isNotificationRemoteFeedback(feedback)) return notificationFeedbackText(feedback);
  return feedback.message !== undefined ? feedback.message : t(($) => $[feedback.key]);
}

export class NotificationFeedbackError extends Error {
  constructor(readonly feedback: NotificationFeedback) {
    super("profileRemote" in feedback ? feedback.fallback : isNotificationRemoteFeedback(feedback) ? feedback.fallback : feedback.message ?? feedback.key);
    this.name = "NotificationFeedbackError";
  }
}
